import {
    Box,
    Button,
    Divider,
    Flex,
    Heading,
    HStack,
    Image,
    Spinner,
    useToast,
    Text,
    VStack,
    useDisclosure,
} from "@chakra-ui/react";
import { FaMapMarkerAlt } from "react-icons/fa";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import { useEffect, useState } from "react";
import RegistrationModal from "./RegistrationModal";
import SuccessModal from "./SuccessModal";
import bg from "../../assets/images/bg.png";
import clock from "../../assets/images/clock.svg";
import loc from "../../assets/images/location.svg";
import img6 from "../../assets/images/img-6.png";
import EventsService from '../../api/services/EventsService';
import {BASE_URL} from "../../config";
import OverlapModal from "./OverlapModal";
import NoCover from "../../components/NoCover/NoCover";

const EventDetailPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const [events, setEvents] = useState([]);
    const [event, setEvent] = useState(null);
    const [eventEndDate, setEventEndDate] = useState(0);
    const [eventStartDate, setEventStartDate] = useState(0);
    const [loading, setLoading] = useState(!event);
    const toast = useToast();
    const baseURL = BASE_URL;

    const {
        isOpen: isRegisterOpen,
        onOpen: onRegisterOpen,
        onClose: onRegisterClose,
    } = useDisclosure();

    const {
        isOpen: isSuccessOpen,
        onOpen: onSuccessOpen,
        onClose: onSuccessClose,
    } = useDisclosure();

    const {
        isOpen: isOverlapOpen,
        onOpen: onOverlapOpen,
        onClose: onOverlapClose,
    } = useDisclosure();

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const res = await EventsService.getAllEvents({
                page: 1,
                pageSize: 4
            });
            const events = res.data.data.items;
            if(events) {
                setEvents(events);
            }
        } catch (error) {
            toast({ title: 'Błąd pobierania danych', status: 'error', duration: 3000, isClosable: true });
        } finally {
            setLoading(false);
        }
    };

    const fetchEvent = async () => {
        window.scrollTo(0, 0);
        setLoading(true);
        try {
            const res = await EventsService.getEvent(id);
            const event = res.data.data;
            if(event) {
                setEvent(event);
                const endDate = new Date(`${event.date}T${event.endTime}:00`).getTime();
                setEventEndDate(endDate);
                const eventStartDate = new Date(`${event.date}T${event.startTime}:00`).getTime();
                setEventStartDate(eventStartDate);
            }
        } catch (error) {
            toast({ title: 'Błąd pobierania danych', status: 'error', duration: 3000, isClosable: true });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!event) {
            fetchEvent();
        }
        fetchEvents();
    }, [event, id]);

    if (loading) {
        return (
            <Flex justify="center" align="center" minH="50vh">
                <Spinner size="xl" />
            </Flex>
        );
    }

    if (!event) return <Text>Brak danych o wydarzeniu.</Text>;

    const handleNavigate = (route, options = {}) => {
        navigate(route, options);
    };

    const handleShowDetails = (event) => {
        handleNavigate(`/event/${event.id}`, { state: { event } });
    };

    const handleEvents = () => {
        handleNavigate("/");
    };

    const formattedDate = (date) => {
        const formattedDate = new Intl.DateTimeFormat('pl-PL', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(date);
        return formattedDate;
    }

    const getImg = (event) => {
        const path = event?.coverImage;
        return path ? `${baseURL}/${path}` : img6;
    }

    const getAvailablePlaces = (event) => {
        const diff = (event.maxParticipants - event.participantsCount);
        return (diff > 0
            ? `Ilość dostępnych miejsc: ${diff}`
            : "Brak dostępnych miejsc");
    }

    const isFull = (event) => {
        return (event.maxParticipants - event.participantsCount) <= 0;
    }

    const isTooLateToRegister = () => {
        const now = new Date();
        const oneHourBeforeStart = new Date(eventStartDate - (60 * 60 * 1000));
        return now.getTime() > oneHourBeforeStart.getTime();
    }

    const isPast = () => {
        const now = new Date();
        return now.getTime() > eventEndDate;
    }

    const isNotAvailable = (event) => {
        return isFull(event) || isPast() || isTooLateToRegister();
    }

    return (
        <>
            <Flex flexDirection={'column'} w={"100%"} alignItems={"center"} justifyContent="center" bgColor={'white'}>
                <Flex bgImg={bg} h={'94px'} w={"100%"} alignItems={"center"} justifyContent="center">
                    <Flex flexDirection={'column'} gap={'16px'} w={{base: '90%', sm: '7xl'}} alignItems={"flex-start"} py={{base: '16px', md: '48px'}} justifyContent={"space-between"}>
                        <Text fontFamily={'Poppins'} fontWeight={'400'} fontSize={{base: '16px', md: '20px'}} lineHeight={'150%'} onClick={() => handleEvents()} cursor={"pointer"}>
                            Wydarzenia > <span style={{fontWeight: 600}}>{event.title}</span>
                        </Text>
                    </Flex>
                </Flex>
                <Flex w={"100%"} alignItems={"center"} justifyContent="center" px={{base: '16px', md: 0}}>
                    <Flex flexDirection={{ base: "column", md: "row" }} gap={'16px'} w={{base: '100%', md: '7xl'}} alignItems={"flex-start"} py={'48px'} justifyContent={"space-between"}>

                        <Flex flexDirection={'column'} gap={'16px'} w="100%">
                            <Flex flexDirection={'column'} gap={'8px'} w="100%">
                                <Text fontFamily={'Poppins'} fontWeight={'700'} fontSize={{base: '20px', md: '24px'}} lineHeight={{base: '150%', md: '100%'}} textAlign={{base: "center", md: "left"}}>
                                    {event.title}
                                </Text>
                                {event.parentEventTitle &&
                                    <Text fontFamily={'Poppins'} fontWeight={'400'} fontSize={{base: '14px', md: '16px'}} lineHeight={'100%'}>
                                        {event.parentEventTitle}
                                    </Text>
                                }
                            </Flex>
                            <Flex gap={'16px'} flexDirection={{base: "column", md: "row"}} w={{base: '100%', md: 'auto'}}>
                                <Flex gap={'8px'} alignItems={"center"} bgColor={'#F0F0F0'} p={'12px'} w={{base: '100%', md: 'auto'}}>
                                    <Image src={clock} alt='clock' w="18px" h="18px" />
                                    <Text fontFamily={'Poppins'} fontWeight={'500'} fontSize={'17px'} lineHeight={'150%'}>
                                        {formattedDate(new Date(event.date))}, {event.startTime} - {event.endTime}
                                    </Text>
                                </Flex>
                                <Flex gap={'8px'} alignItems={"center"} bgColor={'#F0F0F0'} p={'12px'}>
                                    <Image src={loc} alt='clock' w="18px" h="18px" />
                                    <Text fontFamily={'Poppins'} fontWeight={'500'} fontSize={'17px'} lineHeight={'150%'}>
                                        {event.address}
                                    </Text>
                                </Flex>
                            </Flex>
                        </Flex>
                        <Flex flexDirection={'column'} justify={{base: "center", md: "flex-end"}} align={{base: "center", md: "flex-end"}} gap={'16px'} w={{base: "100%", md: "auto"}}>
                            <Text fontFamily={'Poppins'} fontWeight={'500'} fontSize={{base: '14px', md: '17px'}} lineHeight={'150%'} style={{'textWrap': 'nowrap'}}>
                                {eventEndDate < Date.now()
                                    ? "Wydarzenie zakończone"
                                    : getAvailablePlaces(event)}
                            </Text>
                            <Button color={'white'} w={'215px'} h={'50px'}
                                bgColor={'#0A4725'} _hover={'#0A4725'} gap={'10px'} borderRadius={"unset"}
                                fontFamily={'Poppins'} fontWeight={'600'} fontSize={'14px'} lineHeight={'100%'}
                                onClick={onRegisterOpen}
                                disabled={isNotAvailable(event)}
                            >
                                {isTooLateToRegister()
                                    ? "Rejestracja zamknięta"
                                    : <>Zapisz się
                                        <svg width="8" height="10" viewBox="0 0 8 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M0 0H3.58481L8 5L3.58481 10H0L4.43544 5L0 0Z" fill="white"/>
                                        </svg>
                                    </>
                                }
                            </Button>
                        </Flex>

                    </Flex>
                </Flex>

                <Flex w={"100%"} alignItems={"center"} justifyContent="center">
                    <Flex flexDirection={{ base: 'column', md: 'row' }} gap={'32px'} w={{base: '100%', sm: '7xl'}} p={{base: '16px', md: 0}}
                          alignItems={"flex-start"} py={'48px'} justifyContent={"space-between"}>
                        {event.coverImage ? (
                            <Image src={getImg(event)} alt={event.title} objectFit="cover" maxW={{ base: '100%', md: '317px' }} h="auto" maxH="317px" />
                        ) : (
                            <NoCover />
                        )}
                        <VStack align="start" spacing={4} flex="1">
                            <Flex flexDirection={'column'} gap={'16px'}>
                                <Text fontFamily={'Poppins'} fontWeight={'600'} fontSize={{ base: "20px", md: "22px" }} lineHeight={'100%'}>
                                    O wydarzeniu
                                </Text>
                                <Text color="#363636" fontFamily={'Poppins'} fontWeight={'300'} fontSize={{base: '14px', md: '17px'}} lineHeight={'150%'} whiteSpace={"pre-wrap"}>
                                    {event.description}
                                </Text>
                            </Flex>
                        </VStack>
                    </Flex>
                </Flex>

                <Flex w={"100%"} alignItems={"center"} justifyContent="center">
                    <Flex flexDirection={'column'} gap={'32px'} w={{base: '100%', sm: '7xl'}} alignItems={"flex-start"} py={'48px'} justifyContent={"space-between"} px={{base: '16px', md: 0}}>
                        <Divider borderColor={'#A2C2B0'} w={{base: '90%', md: '100%'}} alignSelf={"center"}/>
                        <Text fontFamily={'Poppins'} fontWeight={'500'} fontSize={{base: '20px', md: '24px'}} lineHeight={'100%'} textAlign={{base: "center", md: "left"}}>
                            Inne wydarzenia które mogą Cię zainteresować
                        </Text>

                        <Flex direction="column" w={"100%"} gap={'16px'}>
                            {events && events.filter( (event) => event.id != id ).map((event) => (
                            <Flex
                                key={event.id}
                                bg="#F0F0F0"
                                boxShadow="none"
                                rounded="none"
                                overflow="hidden"
                                direction={{ base: "column", md: "row" }}
                                w={"100%"} justifyContent={"space-between"}
                            >
                                {event.coverImage ? (
                                    <Image src={getImg(event)} alt={event.title} objectFit="cover" w={{base: "100%", md: "107px"}} h="auto" maxH={{base: 'auto', md: "107px"}} />
                                ) : (
                                    <NoCover size="107px" title="Brak zdjęcia" padding={1}/>
                                )}
                                <Box p={'24px'} flexDirection="column" justifyContent="space-between" w={"100%"}>
                                    <Flex alignItems={{base: "left", md: "center"}} flexDirection={{ base: "column", md: "row" }} gap={'24px'} color={'#363636'} justifyContent={"space-between"}>
                                        <Flex flexDirection={"column"} gap={'12px'}>
                                            <Heading fontFamily={'Poppins'} fontWeight={'600'} fontSize={{ base: "20px", md: "22px" }}>
                                                {event.title}
                                            </Heading>
                                            {event.parentEventTitle &&
                                                <Text fontFamily={'Poppins'} fontWeight={'400'} fontSize={{base: '14px', md: '16px'}}>
                                                    {event.parentEventTitle}
                                                </Text>
                                            }
                                        </Flex>
                                        <Flex>
                                            <Button bg={'#0A4725'} _hover={{bgColor: '#1A7743'}} borderRadius={0}
                                                    color={"white"} size="sm" w={{base: '100%', md: '233px'}} h={'56px'} gap={'10px'} alignSelf={"end"}
                                                    fontFamily={'Poppins'} fontWeight={'600'}
                                                    onClick={() => handleShowDetails(event)}>
                                                Dowiedz się więcej
                                                <svg width="8" height="10" viewBox="0 0 8 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M0 0H3.58481L8 5L3.58481 10H0L4.43544 5L0 0Z" fill="white"/>
                                                </svg>
                                            </Button>
                                        </Flex>
                                    </Flex>
                                </Box>
                            </Flex>
                            ))}

                        </Flex>
                        <Button bg={'white'} _hover={{bgColor: 'white'}} w={'215px'} h={'48px'} color={"white"} borderRadius='0' gap={'10px'} color={'#0A4725'}
                                fontFamily={'Poppins'} fontWeight={'600'} fontSize={'14px'} border={'2px'} borderColor={'#0A4725'} alignSelf={"center"}
                                onClick={() => handleEvents()}
                        >
                            Pełna lista wydarzeń
                            <svg width="9" height="10" viewBox="0 0 9 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0.5 0H4.08481L8.5 5L4.08481 10H0.5L4.93544 5L0.5 0Z" fill="#0A4725"/>
                            </svg>
                        </Button>
                    </Flex>
                </Flex>

                {/* Modale */}
                <RegistrationModal
                    isOpen={isRegisterOpen}
                    onClose={onRegisterClose}
                    onSuccess={() => {
                        onRegisterClose();
                        onSuccessOpen();
                    }}
                    onOverlap={onOverlapOpen}
                    event={event}
                />

                <SuccessModal
                    isOpen={isSuccessOpen}
                    onClose={onSuccessClose}
                    eventTitle={event.title}
                />

                <OverlapModal
                    isOpen={isOverlapOpen}
                    onClose={onOverlapClose}
                    eventTitle={event.title}
                />
            </Flex>
        </>
    );
};

export default EventDetailPage;
